import {
	TableColumnDefinition,
	useTableFeatures,
	TableBody,
	TableRow,
	TableCell,
	TableHeader,
	useTableSort,
	TableColumnId,
	SortDirection,
	TableColumnSizingOptions,
	createTableColumn,
	UseTableFeaturesOptions,
	useTableColumnSizing_unstable,
	TableRowId,
	useTableSelection,
	TableSelectionCell,
	mergeClasses,
	Table,
	Field,
	Input,
	Button,
} from "@fluentui/react-components";
import { Search24Regular, FilterDismiss24Regular } from "@fluentui/react-icons";
import * as React from "react";
import styles from "./PCSTable.module.scss";
import _ from "lodash";
import { FilterValue, IColumnDefinition } from "./PCSTableTypes";
import { calculateColumns, getColumnFilterValue, getFilterValues } from "./PCSTableHelpers";
import { PCSTableHeaderCell } from "./PCSTableHeaderCell";
import { PCSTableCell } from "./PCSTableCell";
import { hasText } from "@/helpers/SearchHelpers";

import { Skeleton, SkeletonItem, SkeletonProps } from "@fluentui/react-skeleton";
interface SortState {
	sortColumn: TableColumnId | undefined;
	sortDirection: SortDirection;
}

export interface ITableProps<T extends object> {
	columns?: IColumnDefinition<T>[];
	items: T[];
	defaultSortState?: SortState;
	getRowId?: (item: T) => TableRowId;
	canSelectRow?: (item: T) => boolean;
	selectionMode?: "single" | "multiselect";
	selectOnRowClick?: boolean;
	selectedItems?: Iterable<TableRowId> | undefined;
	defaultSelectedItems?: Iterable<TableRowId> | undefined;
	onSelectionChange?: (items: Iterable<TableRowId>) => void;
	onItemInvoked?: (item: T) => void;
	getRowClass?: (item: T) => string | null | undefined;
	defaultFilters?: FilterValue[];
	filters?: FilterValue[];
	onFiltersChanged?: (filters: FilterValue[]) => void;
	disableSelection?: boolean;
	subtleSelection?: boolean;
	enableShimmer?: boolean;
	shimmerLocation?: "top" | "bottom";
	shimmerRows?: number;
	stickyHeader?: boolean;
	maxHeight?: number;
	searchable?: boolean;
	onSearch?: (searchText: string, item: T) => boolean;
}

export function PCSTable<T extends object>({
	columns: cols,
	items,
	defaultSortState,
	getRowId,
	selectionMode,
	canSelectRow,
	selectOnRowClick,
	defaultSelectedItems,
	selectedItems,
	onSelectionChange,
	onItemInvoked,
	getRowClass,
	defaultFilters,
	filters: filtersIn,
	onFiltersChanged,
	disableSelection,
	subtleSelection,
	enableShimmer,
	shimmerRows,
	stickyHeader,
	maxHeight,
	searchable,
	onSearch,
	shimmerLocation,
}: ITableProps<T>) {
	const [filters, setFilters] = React.useState<FilterValue[]>(defaultFilters || []);
	const [searchText, setSearchText] = React.useState("");
	const [debouncedSearchText, setDebouncedSearchText] = React.useState("");

	React.useEffect(() => {
		const to = setTimeout(() => {
			setDebouncedSearchText(searchText);
		}, 500);

		return () => {
			clearTimeout(to);
		};
	}, [searchText]);

	const shimmerRowElments = React.useMemo(() => {
		if (!enableShimmer) return [];

		return Array.from(Array(shimmerRows ?? 10).keys());
	}, [shimmerRows, enableShimmer]);

	React.useEffect(() => {
		if (filtersIn) {
			setFilters(filtersIn);
		}
	}, [filtersIn]);

	const columns: IColumnDefinition<T>[] = React.useMemo<IColumnDefinition<T>[]>(() => {
		if (!cols?.length) {
			return calculateColumns<T>(items);
		}
		return cols;
	}, [cols, items]);

	const columnSizingOptions = React.useMemo<TableColumnSizingOptions>(() => {
		const sizing: TableColumnSizingOptions = {};
		columns.forEach((i) => {
			if (i.sizing) {
				sizing[i.columnId] = { ...i.sizing };
			}
		});
		return sizing;
	}, [columns]);
	const filteredItems = React.useMemo(() => {
		if (filters.length === 0 && (!searchable || !debouncedSearchText)) return items;
		let filteredItems = [...items];
		const search = debouncedSearchText.trim();
		if (!!search) {
			const search = debouncedSearchText.trim();

			if (onSearch) filteredItems = filteredItems.filter((i) => onSearch(search, i));
			else {
				filteredItems = filteredItems.filter((i) => hasText({ ...i }, search, true));
			}
		}
		if (!filters.length) return filteredItems;

		const filteredColumns = _.unionBy(
			filters.map((filt) => columns.find((col) => col.columnId === filt.columnId)),
			(i) => i?.columnId
		);
		const byCol = _.groupBy(filters, (i) => i.columnId);

		return filteredItems.filter((item) => {
			return filteredColumns
				.map((column) => {
					if (!column || !byCol[column.columnId]?.length) return [true];
					const colFilters = byCol[column.columnId];
					const colFiltVal = getColumnFilterValue(item, column);
					const match = colFilters.some(
						(i) => i.columnId === colFiltVal.columnId && i.value === colFiltVal.value
					);

					return match;
				})
				.every((j) => j);
		});
	}, [filters, items, columns, debouncedSearchText, searchable, onSearch]);

	const mappedCols: TableColumnDefinition<T>[] = React.useMemo(() => {
		return columns
			.filter((i) => !i.hidden)
			.map((i) =>
				createTableColumn<T>({
					columnId: i.columnId,
					renderHeaderCell: () => i.headerText,
					compare(a, b) {
						if (i.compare) {
							return i.compare(a, b);
						}

						if (!i.getValue) return 0;

						const valA = i.getValue(a) ?? "";
						const valB = i.getValue(b) ?? "";

						if (!valA && !valB) return 0;

						if (valA! > valB!) return 1;
						else if (valA! < valB!) return -1;
						else return 0;
					},
				})
			);
	}, [columns]);

	const options = React.useMemo<UseTableFeaturesOptions<T>>(
		() => ({
			columns: mappedCols,
			items: filteredItems,
			getRowId,
		}),
		[filteredItems, mappedCols, getRowId]
	);

	const {
		getRows,
		sort: { getSortDirection, toggleColumnSort, sort, setColumnSort },
		selection,
		columnSizing_unstable: columnSizing,
		columns: colsInternal,
	} = useTableFeatures<T>(options, [
		useTableSort({
			defaultSortState,
		}),
		useTableSelection({
			selectionMode: selectionMode ?? "single",
			selectedItems,
			defaultSelectedItems,
			onSelectionChange(e, data) {
				if (onSelectionChange) {
					onSelectionChange(data.selectedItems);
				}
			},
		}),
		useTableColumnSizing_unstable({ columnSizingOptions }),
	]);

	const rows = sort(
		getRows((row) => {
			const selected = selection.isRowSelected(row.rowId);
			const selectable = !!selectionMode && (!canSelectRow || canSelectRow(row.item));
			return {
				selected,
				selectable,
				item: row.item,
				rowId: row.rowId,
			};
		})
	);

	const updateFilters = React.useCallback(
		(filters: FilterValue[]) => {
			onFiltersChanged ? onFiltersChanged(filters) : setFilters(filters);
		},
		[onFiltersChanged]
	);

	const toggleFilter = React.useCallback(
		(i: FilterValue, column: IColumnDefinition<T>) => {
			if (filters.some((j) => j.key === i.key && i.columnId === column.columnId)) {
				const otherCols = filters.filter((j) => j.columnId !== column.columnId);
				const nv = filters.filter((j) => j.key !== i.key && i.columnId === column.columnId);
				updateFilters([...otherCols, ...nv]);
			} else {
				updateFilters([...filters, i]);
			}
		},
		[filters, updateFilters]
	);

	const setColumnFilters = React.useCallback(
		(i: FilterValue[], column: IColumnDefinition<T>) => {
			const otherCols = filters.filter((j) => j.columnId !== column.columnId);
			updateFilters([...otherCols, ...i]);
		},
		[filters, updateFilters]
	);

	const shimmer = React.useMemo(() => {
		return enableShimmer ? (
			<TableBody>
				{shimmerRowElments.map((i) => {
					return (
						<TableRow key={i}>
							{cols
								?.filter((i) => !i.hidden)
								.map((column) => (
									<TableCell key={column.columnId}>
										<Skeleton width={"100%"}>
											<SkeletonItem></SkeletonItem>
										</Skeleton>
									</TableCell>
								))}
						</TableRow>
					);
				})}
			</TableBody>
		) : (
			<></>
		);
	}, [enableShimmer, shimmerRowElments, cols]);
	const shimmerTop = React.useMemo(() => {
		return enableShimmer && shimmerLocation === "top" ? true : false;
	}, [enableShimmer, shimmerRowElments, cols]);
	return (
		<div
			className={mergeClasses(styles.pcsTable, stickyHeader && styles.stickyHeader)}
			style={{ maxHeight }}>
			{searchable && (
				<div className={styles.toolbar}>
					<div className={styles.searchBox}>
						<Field label={"Filter"} className={styles.field}>
							<Input
								value={searchText}
								contentBefore={<Search24Regular />}
								placeholder="Filter"
								onChange={(ev, val) => setSearchText(val.value || "")}></Input>
						</Field>
					</div>

					<div className={styles.filterButton}>
						<Field>
							<Button
								onClick={() => {
									setSearchText("");
									setDebouncedSearchText("");
									setFilters([]);
								}}
								disabled={filters.length === 0 && !searchText}
								appearance="primary"
								icon={<FilterDismiss24Regular></FilterDismiss24Regular>}>
								Clear Filters
							</Button>
						</Field>
					</div>
				</div>
			)}
			<Table sortable size="small">
				<TableHeader>
					<TableRow>
						{!!selectionMode && (
							<TableSelectionCell
								subtle={subtleSelection}
								checked={
									selection.allRowsSelected ? true : selection.someRowsSelected ? "mixed" : false
								}
								checkboxIndicator={{ disabled: disableSelection }}
								onClick={selection.toggleAllRows}></TableSelectionCell>
						)}

						{columns
							.filter((i) => !i.hidden)
							.map((column, index) => {
								const col = colsInternal[index];
								const headerSizing = !!column.sizing
									? columnSizing.getTableHeaderCellProps(column.columnId)
									: {};
								const colFilters = getFilterValues(items, column);
								const selectedFilters = colFilters.filter(
									(i) => !!filters.find((j) => i.columnId === column.columnId && i.key === j.key)
								);

								return (
									<PCSTableHeaderCell
										key={column.columnId}
										columnDefinition={column}
										filters={colFilters}
										getSortDirection={getSortDirection}
										headerSizing={headerSizing}
										setColumnSort={setColumnSort}
										tabeleColumnDefinition={col}
										selectedFilters={selectedFilters}
										toggleColumnSort={toggleColumnSort}
										toggleFilter={toggleFilter}
										setColumnFilters={setColumnFilters}
									/>
								);
							})}
					</TableRow>
				</TableHeader>
				{shimmerTop && shimmer}
				<TableBody>
					{rows.map(({ item, rowId, selected, selectable }) => (
						<TableRow
							className={(getRowClass && getRowClass(item)) ?? ""}
							key={rowId}
							onDoubleClick={() => {
								!!onItemInvoked && onItemInvoked(item);
							}}
							onClick={(e) => {
								if (selectOnRowClick && selectable && !disableSelection) {
									selection.toggleRow(e, rowId);
								}
							}}>
							{selectable ? (
								<TableSelectionCell
									checked={selected}
									subtle={subtleSelection && !!selectionMode}
									onClick={(e) => {
										if (!disableSelection) {
											selection.toggleRow(e, rowId);
											e.stopPropagation();
										}
									}}
									checkboxIndicator={{ "aria-label": "Select row", disabled: disableSelection }}
								/>
							) : (
								!!selectionMode && <TableCell></TableCell>
							)}
							{columns
								.filter((i) => !i.hidden)
								.map((column) => {
									const sizing = !!column.sizing
										? columnSizing.getTableCellProps(column.columnId)
										: {};

									return (
										<PCSTableCell<T>
											key={column.columnId}
											column={column}
											sizing={sizing}
											item={item}></PCSTableCell>
									);
								})}
						</TableRow>
					))}
				</TableBody>
				{!shimmerTop && shimmer}
			</Table>
		</div>
	);
}
